import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import './gallery-showcase.css'

// Classroom
import Classroom01 from '../../../assets/img/gallery-new/gallery-classroom-01.webp'
import Classroom02 from '../../../assets/img/gallery-new/gallery-classroom-02.webp'
import Classroom03 from '../../../assets/img/gallery-new/gallery-classroom-03.webp'
import Classroom04 from '../../../assets/img/gallery-new/gallery-classroom-04.webp'
import Classroom05 from '../../../assets/img/gallery-new/gallery-classroom-05.webp'
import Classroom06 from '../../../assets/img/gallery-new/gallery-classroom-06.webp'
// Laboratory
import Laboratory01 from '../../../assets/img/gallery-new/gallery-laboratory-01.webp'
import Laboratory02 from '../../../assets/img/gallery-new/gallery-laboratory-02.webp'
// Library
import Library01 from '../../../assets/img/gallery-new/gallery-library-01.webp'
import Library02 from '../../../assets/img/gallery-new/gallery-library-02.webp'
import Library03 from '../../../assets/img/gallery-new/gallery-library-03.webp'
import Library04 from '../../../assets/img/gallery-new/gallery-library-04.webp'
import Library05 from '../../../assets/img/gallery-new/gallery-library-05.webp'
import Library06 from '../../../assets/img/gallery-new/gallery-library-06.webp'
// Campus Life
import Campus01 from '../../../assets/img/gallery-new/gallery-campus-01.webp'
import Campus02 from '../../../assets/img/gallery-new/gallery-campus-02.webp'

const galleryItems = [
  { id: 1, src: Classroom01, category: 'Classroom', title: 'Class Notes', caption: 'Students working through their notes during a lecture session.' },
  { id: 2, src: Classroom02, category: 'Classroom', title: 'Interactive Session', caption: 'A faculty member engages students in discussion, with reference material on screen.' },
  { id: 3, src: Classroom03, category: 'Classroom', title: 'Attentive Classroom', caption: 'Students following a lecture in a sunlit classroom.' },
  { id: 4, src: Classroom04, category: 'Classroom', title: 'In Class', caption: 'Students following the lecture closely from their desks.' },
  { id: 5, src: Classroom05, category: 'Classroom', title: 'Full Classroom', caption: 'A faculty member addresses the class from the front of the room.' },
  { id: 6, src: Classroom06, category: 'Classroom', title: 'Taking Notes', caption: 'A student noting down key points during a lecture.' },
  { id: 7, src: Laboratory01, category: 'Laboratory', title: 'Lab Discussion', caption: 'Students discussing an experiment at the pharmacognosy lab bench.' },
  { id: 8, src: Laboratory02, category: 'Laboratory', title: 'Titration Practical', caption: 'A student sets up a titration while a classmate looks on.' },
  { id: 9, src: Library01, category: 'Library', title: 'Browsing the Stacks', caption: 'Students browsing the reference shelves, reading standing up between the aisles.' },
  { id: 10, src: Library02, category: 'Library', title: 'Library Reference', caption: 'Students going through a reference book together in the library.' },
  { id: 11, src: Library03, category: 'Library', title: 'Quiet Reading', caption: 'A student reading through a textbook among the library shelves.' },
  { id: 12, src: Library04, category: 'Library', title: 'Library Portrait', caption: 'A student with her Essentials of Medical Pharmacology textbook.' },
  { id: 13, src: Library05, category: 'Library', title: 'Group Study Circle', caption: 'A group of students gathers around a textbook at the library table.' },
  { id: 14, src: Library06, category: 'Library', title: 'Study Session', caption: 'A smaller group works through a textbook passage together.' },
  { id: 15, src: Campus01, category: 'Campus Life', title: 'Campus Walk', caption: 'Two students walking down the steps of the college building.' },
  { id: 16, src: Campus02, category: 'Campus Life', title: 'Between Classes', caption: 'Students heading down the campus steps between classes.' },
]

const categories = ['All', 'Classroom', 'Laboratory', 'Library', 'Campus Life']

const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
  exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2 } },
}

function GalleryShowcase() {
  const [activeCategory, setActiveCategory] = useState('All')
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const touchStartX = useRef(null)

  const filteredItems =
    activeCategory === 'All'
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeCategory)

  const openLightbox = (index) => setLightboxIndex(index)
  const closeLightbox = () => setLightboxIndex(null)

  const showPrev = useCallback(() => {
    setLightboxIndex((prev) =>
      prev === null ? null : (prev - 1 + filteredItems.length) % filteredItems.length
    )
  }, [filteredItems.length])

  const showNext = useCallback(() => {
    setLightboxIndex((prev) =>
      prev === null ? null : (prev + 1) % filteredItems.length
    )
  }, [filteredItems.length])

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowLeft') showPrev()
      if (e.key === 'ArrowRight') showNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [lightboxIndex, showPrev, showNext])

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX
  }

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartX.current
    const swipeThreshold = 50
    if (deltaX > swipeThreshold) showPrev()
    else if (deltaX < -swipeThreshold) showNext()
    touchStartX.current = null
  }

  const handleCategoryChange = (cat) => {
    if (cat === activeCategory) return
    setActiveCategory(cat)
  }

  const activeItem = lightboxIndex !== null ? filteredItems[lightboxIndex] : null

  return (
    <section className="gallery-showcase-area">
      <div className="container">
        {/* Filter Pills */}
        <div className="gallery-filter-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`gallery-filter-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => handleCategoryChange(cat)}
            >
              {activeCategory === cat && (
                <motion.span
                  layoutId="filter-pill"
                  className="gallery-filter-pill"
                  transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                />
              )}
              <span className="gallery-filter-label">{cat}</span>
            </button>
          ))}
        </div>

        {/* Masonry Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            className="gallery-masonry"
            variants={gridVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {filteredItems.map((item, index) => (
              <motion.div
                className="gallery-masonry-item"
                key={item.id}
                variants={itemVariants}
                layout
                onClick={() => openLightbox(index)}
              >
                <img src={item.src} alt={item.title} loading="lazy" />
                <div className="gallery-item-overlay">
                  <span className="gallery-item-tag">{item.category}</span>
                  <h5 className="gallery-item-title">{item.title}</h5>
                  <span className="gallery-zoom-icon">
                    <i className="fas fa-search-plus"></i>
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {activeItem && (
          <motion.div
            className="gallery-lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeLightbox}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <button className="gallery-lightbox-close" onClick={closeLightbox} aria-label="Close">
              <i className="fas fa-times"></i>
            </button>

            <button
              className="gallery-lightbox-arrow gallery-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); showPrev() }}
              aria-label="Previous image"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            <motion.div
              className="gallery-lightbox-content"
              key={activeItem.id}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={activeItem.src} alt={activeItem.title} />
              <div className="gallery-lightbox-info">
                <div>
                  <span className="gallery-lightbox-tag">{activeItem.category}</span>
                  <h4>{activeItem.title}</h4>
                  <p>{activeItem.caption}</p>
                </div>
                <span className="gallery-lightbox-counter">
                  {lightboxIndex + 1} / {filteredItems.length}
                </span>
              </div>
            </motion.div>

            <button
              className="gallery-lightbox-arrow gallery-lightbox-next"
              onClick={(e) => { e.stopPropagation(); showNext() }}
              aria-label="Next image"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default GalleryShowcase
